<template>
  <div class="position-relative">
    <div
      v-if="active"
      ref="container"
      :aria-busy="active"
      aria-label="Loading"
      class="align-items-center d-flex justify-content-center position-fixed top-0 left-0 w-100 h-100 z-[9999]"
      data-loading
      tabindex="0"
    >
      <div class="bg-white w-100 h-100 opacity-50 position-fixed" />
      <div class="position-relative">
        <svg
          class="block"
          fill="none"
          height="80"
          viewBox="0 0 246 179"
          width="120"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            id="loading-path"
            ref="loadingPath"
            d="M177.459 169.638L224.959 121.138C233.459 109.138 245.359 80.0378 224.959 59.6378C204.559 39.2378 188.125 22.8044 182.459 17.1378C170.459 10.1378 140.959 1.63777 118.959 23.6378C96.9588 45.6378 75.1255 67.4711 66.9588 75.6378C62.2922 80.4711 56.2588 93.4377 69.4588 106.638C82.6588 119.838 91.6254 118.471 94.4588 116.138L138.959 71.6377C143.625 65.971 156.359 58.0377 169.959 71.6377C186.959 88.6377 186.968 90.1186 177.459 99.6282C169.851 107.236 135.289 141.798 118.959 158.128C107.625 167.131 78.5587 178.738 52.9588 153.138C27.3589 127.538 37.4591 137.638 21.4592 121.638C6.45897 101.138 4.95947 75.6378 20.4588 56.1377C35.9461 36.6527 59.4588 17.1377 67.9588 8.6377"
            stroke="#000"
            stroke-width="19"
          />
        </svg>
      </div>
    </div>
    <slot></slot>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, Ref, watch, nextTick } from "vue";
import gsap from "gsap";

const props = defineProps({ active: { type: Boolean } });
const container: Ref<HTMLDivElement | null> = ref(null);
const loadingPath: Ref<SVGPathElement | null> = ref(null);
let tl: any = null;

function focusIn(event: FocusEvent) {
  if (!props.active || event.target === null || container.value === null) return;
  const target = event.target as HTMLElement;
  if (target === container.value || container.value.contains(target)) return;
  const parent = container.value.parentElement;
  if (parent && parent.contains(target)) {
    event.preventDefault();
    container.value.focus();
  }
}

function startAnimation() {
  if (!loadingPath.value) return;

  const path = loadingPath.value;
  const length = path.getTotalLength();

  gsap.set(path, {
    strokeDasharray: length,
    strokeDashoffset: length,
  });

  tl = gsap.timeline({
    repeat: -1, // Repete infinitamente
    repeatDelay: 0.2, // pausa Antes do loop
  });

  // Animação 1: Desenha a linha
  tl.to(path, {
    strokeDashoffset: 0,
    duration: 1.5,
    ease: "power2.inOut",
  })
    // Animação 2: Apaga a linha
    .to(path, {
      strokeDashoffset: -length,
      duration: 1.5,
      ease: "power2.inOut",
      delay: 0.1, // pausa com a linha completa
    });
}

watch(
  () => props.active,
  async (val) => {
    if (val) {
      await nextTick();
      startAnimation();
    } else {
      if (tl) tl.kill();
    }
  },
  { immediate: true },
);

onMounted(() => document.addEventListener("focusin", focusIn));
onBeforeUnmount(() => {
  document.removeEventListener("focusin", focusIn);
  if (tl) tl.kill();
});
</script>

<style scoped>
.z-\[9999\] {
  z-index: 9999;
}
.position-fixed {
  position: fixed;
}
.w-100 {
  width: 100%;
}
.h-100 {
  height: 100%;
}
.top-0 {
  top: 0;
}
.left-0 {
  left: 0;
}
</style>
